import { appConfig } from 'config';
import { http } from 'services/http';
/** Talks to the real onboard-service assistant API. */
export class RealAiAssistantClient {
    options;
    constructor(options) {
        this.options = options;
    }
    async createSession() {
        return http({
            method: 'POST',
            url: '/assistant/sessions',
            accessToken: this.options.accessToken
        });
    }
    // Bypasses `http()` since it's axios-based and can't read the SSE reply incrementally.
    async sendMessage({ sessionId, text, jobId, onToken, signal }) {
        const response = await fetch(`${appConfig.onboardApi}/api/assistant/sessions/${sessionId}/messages`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                Authorization: `Bearer ${this.options.accessToken}`
            },
            body: JSON.stringify({ message: text, jobId }),
            signal
        });
        if (!response.ok) {
            const errorBody = (await response
                .json()
                .catch(() => null));
            throw new Error(errorBody?.message ?? `Assistant request failed (${response.status})`);
        }
        if (!response.body) {
            throw new Error('Assistant response had no body to stream');
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let fullText = '';
        // TODO: add a client-side inactivity timeout — if the connection dies
        // without a close/error, `reader.read()` never settles and the composer
        // locks up forever (server-side DEFAULT_STREAM_TIMEOUT_MS doesn't help here).
        for (;;) {
            // Sequential by nature — each read continues the same single stream.
            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();
            if (done)
                break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';
            for (const line of lines) {
                if (!line.startsWith('data: '))
                    continue;
                const payload = line.slice('data: '.length).trim();
                if (!payload)
                    continue;
                const event = JSON.parse(payload);
                if (event.type === 'token') {
                    fullText += event.text;
                    onToken(fullText);
                }
                else if (event.type === 'done') {
                    // `done` only carries `messageId`; timestamp isn't sent, so synthesize it.
                    return {
                        id: event.messageId,
                        role: 'assistant',
                        content: fullText,
                        createdAt: new Date().toISOString()
                    };
                }
                else {
                    throw new Error(event.message);
                }
            }
        }
        throw new Error('Assistant stream ended unexpectedly');
    }
}
